import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import ChoiceCard from "@/components/ChoiceCard";
import LeadCapture from "@/components/LeadCapture";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  STEPS,
  USAGE_TYPES,
  CAR_SIZES,
  CONDITIONS,
  TRANSMISSIONS,
  FUEL_TYPES,
  BRAND_TIERS,
  ANNUAL_KM_OPTIONS,
  BUDGET_OPTIONS,
  BODY_TYPES,
  WHEEL_DRIVE_OPTIONS,
  DOORS_OPTIONS,
  SEATS_OPTIONS,
} from "@/lib/wizardData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  annual_km: 0,
  car_size: "",
  city: "",
  usage_type: "",
  condition: "",
  budget_eur: 0,
  transmission: "",
  new_driver: false,
  fuel_type: "",
  brand_tier: "",
  body_type: "any",
  wheel_drive: "any",
  doors: "any",
  seats: "any",
};

function euro(n) {
  return new Intl.NumberFormat("it-IT").format(n);
}

export default function Wizard() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [errorInfo, setErrorInfo] = useState(null);
  const navigate = useNavigate();

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const stepValid = {
    1: form.usage_type && form.car_size && form.city.trim().length > 1 && form.annual_km > 0,
    2: form.transmission && form.fuel_type,
    3: form.condition && form.brand_tier && form.budget_eur > 0,
    4: true, // all optional
  };

  const progress = (step / STEPS.length) * 100;

  const goNext = () => {
    if (!stepValid[step]) {
      toast.error("Completa tutte le scelte per continuare");
      return;
    }
    if (step < STEPS.length) {
      setStep(step + 1);
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
    else handleSubmit();
  };

  const goBack = () => {
    if (step > 1) {
      setStep(step - 1);
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setErrorInfo(null);
    try {
      const { data } = await axios.post(`${API}/recommend`, form, {
        timeout: 90000,
        withCredentials: true,
      });
      setResultData(data);
      setSubmitting(false);
    } catch (e) {
      setSubmitting(false);
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      // Structured hint from backend validation
      if (detail && typeof detail === "object" && detail.title) {
        setErrorInfo({
          title: detail.title,
          message: detail.message,
          suggestions: detail.suggestions || [],
        });
        return;
      }
      // Timeout / network / server error
      if (!e.response || status === 504 || status === 502 || e.code === "ECONNABORTED") {
        setErrorInfo({
          title: "La ricerca sta impiegando troppo",
          message:
            "I nostri consulenti stanno impiegando più tempo del previsto per trovare le auto giuste. Spesso è perché la combinazione richiesta è molto specifica.",
          suggestions: [
            "Prova ad ampliare la fascia di budget (una fascia più alta o più bassa)",
            "Cambia l'alimentazione in \"Indifferente\" per avere più risultati",
            "Se hai selezionato \"Luxury\", prova \"Premium\": tempi di risposta molto più rapidi",
            "Riprova tra qualche secondo — a volte è solo un rallentamento temporaneo",
          ],
        });
        return;
      }
      toast.error(typeof detail === "string" ? detail : "Errore imprevisto. Riprova.");
    }
  };

  return (
    <div className="min-h-screen" data-testid="wizard-page">
      <header className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="font-heading text-xl font-semibold tracking-tight" data-testid="wizard-brand-link">
          auto<span className="text-[#0055FF]">.</span>advisor
        </Link>
        <span className="text-sm text-[#666]" data-testid="step-indicator">
          Passo {step} di {STEPS.length}
        </span>
      </header>

      <div className="max-w-4xl mx-auto px-6">
        <div className="h-1.5 w-full bg-[#EAEAEA] rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-[#0055FF]"
            initial={false}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            data-testid="progress-bar"
          />
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-6 py-10 md:py-14">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
          >
            <div className="mb-10">
              <p className="text-sm uppercase tracking-widest text-[#0055FF] font-medium">
                {STEPS[step - 1].subtitle}
              </p>
              <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-semibold tracking-tight">
                {STEPS[step - 1].title}
              </h1>
            </div>

            {step === 1 && (
              <Step1 form={form} update={update} />
            )}
            {step === 2 && (
              <Step2 form={form} update={update} />
            )}
            {step === 3 && (
              <Step3 form={form} update={update} />
            )}
            {step === 4 && (
              <Step4 form={form} update={update} />
            )}
          </motion.div>
        </AnimatePresence>

        <div className="mt-14 flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={goBack}
            disabled={step === 1 || submitting}
            className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-[#EAEAEA] bg-white text-[#111] hover:border-[#111] transition disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="wizard-back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Indietro
          </button>

          <button
            type="button"
            onClick={goNext}
            disabled={submitting}
            className="group inline-flex items-center gap-2 h-14 px-8 rounded-full bg-[#111] text-white hover:bg-[#333] transition disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid={step === STEPS.length ? "wizard-submit-button" : "wizard-next-button"}
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Analizzando le tue preferenze…
              </>
            ) : step === STEPS.length ? (
              <>Conferma e trova auto <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></>
            ) : (
              <>Avanti <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></>
            )}
          </button>
        </div>
      </main>

      {submitting && <LoadingOverlay />}
      {errorInfo && (
        <ErrorHelpModal info={errorInfo} onClose={() => setErrorInfo(null)} />
      )}
      {resultData && (
        <div className="fixed inset-0 z-50 bg-[#F9F9FB] overflow-y-auto" data-testid="lead-gate">
          <div className="max-w-3xl mx-auto px-6 py-14">
            <span className="text-xs uppercase tracking-widest text-[#0055FF] font-medium">Ultimo passo</span>
            <h1 className="mt-3 font-heading text-3xl sm:text-4xl font-semibold tracking-tight">
              Le tue {resultData.recommendations.length} auto sono pronte
            </h1>
            <p className="mt-3 text-[#666] max-w-xl">
              Lasciaci i tuoi contatti per visualizzare la selezione. Un consulente potrà proporti offerte reali dai concessionari partner della tua zona.
            </p>
            <LeadCapture
              recommendationId={resultData.id}
              onSuccess={() => navigate(`/results/${resultData.id}`, { state: resultData })}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, description, children, testId }) {
  return (
    <section className="mb-12" data-testid={testId}>
      <div className="mb-4">
        <h2 className="font-heading text-xl font-medium">{title}</h2>
        {description && <p className="mt-1 text-sm text-[#666]">{description}</p>}
      </div>
      {children}
    </section>
  );
}

function CardGrid({ options, value, onSelect, prefix }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {options.map((opt) => (
        <ChoiceCard
          key={opt.value}
          option={opt}
          selected={value === opt.value}
          onSelect={onSelect}
          testId={`${prefix}-${opt.value}-card`}
        />
      ))}
    </div>
  );
}

function Step1({ form, update }) {
  return (
    <>
      <Section title="Tipo di utilizzo" description="Come userai principalmente l'auto?" testId="section-usage-type">
        <CardGrid
          options={USAGE_TYPES}
          value={form.usage_type}
          onSelect={(v) => update("usage_type", v)}
          prefix="usage"
        />
      </Section>

      <Section title="Dimensione dell'auto" description="Quanto spazio ti serve davvero?" testId="section-car-size">
        <CardGrid
          options={CAR_SIZES}
          value={form.car_size}
          onSelect={(v) => update("car_size", v)}
          prefix="size"
        />
      </Section>

      <Section title="La tua città" description="Ci aiuta a considerare ZTL, blocchi Euro e infrastruttura ricarica." testId="section-city">
        <Input
          value={form.city}
          onChange={(e) => update("city", e.target.value)}
          placeholder="Es. Milano, Roma, Bari…"
          className="h-14 rounded-2xl border-[#EAEAEA] bg-white text-base px-5 focus-visible:ring-[#0055FF] focus-visible:ring-1 focus-visible:ring-offset-0"
          data-testid="input-city"
        />
      </Section>

      <Section
        title="Chilometri percorsi all'anno"
        description="Un dato chiave per scegliere motorizzazione e alimentazione."
        testId="section-annual-km"
      >
        <CardGrid
          options={ANNUAL_KM_OPTIONS}
          value={form.annual_km}
          onSelect={(v) => update("annual_km", v)}
          prefix="km"
        />
      </Section>
    </>
  );
}

function Step2({ form, update }) {
  return (
    <>
      <Section title="Cambio" description="Manuale o automatico? Cambia molto in città." testId="section-transmission">
        <CardGrid
          options={TRANSMISSIONS}
          value={form.transmission}
          onSelect={(v) => update("transmission", v)}
          prefix="transmission"
        />
      </Section>

      <Section title="Alimentazione" description="La scelta più importante per costi ed emissioni." testId="section-fuel">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FUEL_TYPES.map((opt) => (
            <ChoiceCard
              key={opt.value}
              option={opt}
              selected={form.fuel_type === opt.value}
              onSelect={(v) => update("fuel_type", v)}
              testId={`fuel-${opt.value}-card`}
            />
          ))}
        </div>
      </Section>

      <Section title="Sei neopatentato?" description="Se sì, applicheremo i limiti di potenza previsti dalla legge italiana." testId="section-new-driver">
        <div className="rounded-2xl border border-[#EAEAEA] bg-white p-5 md:p-6 flex items-center justify-between">
          <div>
            <Label htmlFor="new-driver-switch" className="font-heading text-lg font-medium">
              Neopatentato
            </Label>
            <p className="mt-1 text-sm text-[#666]">
              Attivalo se hai la patente da meno di 3 anni. Le auto suggerite rispetteranno i limiti (max 95 kW e 55 kW/t).
            </p>
          </div>
          <Switch
            id="new-driver-switch"
            checked={form.new_driver}
            onCheckedChange={(v) => update("new_driver", v)}
            data-testid="switch-new-driver"
          />
        </div>
      </Section>
    </>
  );
}

function Step3({ form, update }) {
  return (
    <>
      <Section title="Nuova o usata?" description="Cambia molto in termini di budget effettivo." testId="section-condition">
        <CardGrid
          options={CONDITIONS}
          value={form.condition}
          onSelect={(v) => update("condition", v)}
          prefix="condition"
        />
      </Section>

      <Section title="Fascia di brand" description="Che tipo di esperienza vuoi?" testId="section-brand-tier">
        <CardGrid
          options={BRAND_TIERS}
          value={form.brand_tier}
          onSelect={(v) => update("brand_tier", v)}
          prefix="brand"
        />
      </Section>

      <Section
        title="Budget massimo"
        description="La fascia di spesa che consideri realistica, tutto incluso."
        testId="section-budget"
      >
        <CardGrid
          options={BUDGET_OPTIONS}
          value={form.budget_eur}
          onSelect={(v) => update("budget_eur", v)}
          prefix="budget"
        />
      </Section>
    </>
  );
}

function Step4({ form, update }) {
  return (
    <>
      <Section title="Tipo di carrozzeria" description="Se hai una preferenza netta, selezionala. Altrimenti lascia Indifferente." testId="section-body-type">
        <ChipGrid options={BODY_TYPES} value={form.body_type} onSelect={(v) => update("body_type", v)} prefix="body" />
      </Section>

      <Section title="Trazione" description="Utile solo se ti serve la 4x4 (montagna, neve, sterrato)." testId="section-wheel-drive">
        <ChipGrid options={WHEEL_DRIVE_OPTIONS} value={form.wheel_drive} onSelect={(v) => update("wheel_drive", v)} prefix="drive" />
      </Section>

      <Section title="Numero di porte" testId="section-doors">
        <ChipGrid options={DOORS_OPTIONS} value={form.doors} onSelect={(v) => update("doors", v)} prefix="doors" />
      </Section>

      <Section title="Numero di posti" description="Utile se hai una famiglia numerosa o servono i 7 posti." testId="section-seats">
        <ChipGrid options={SEATS_OPTIONS} value={form.seats} onSelect={(v) => update("seats", v)} prefix="seats" />
      </Section>

      <div className="rounded-2xl bg-[#E5EEFF] p-4 text-sm text-[#0055FF]">
        Tutti questi filtri sono <b>opzionali</b>. Puoi lasciare "Indifferente" e i nostri consulenti sceglieranno le auto in base alle preferenze principali dei passi precedenti.
      </div>
    </>
  );
}

function ChipGrid({ options, value, onSelect, prefix }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={`chip-grid-${prefix}`}>
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onSelect(opt.value)}
            className={`h-11 px-4 rounded-full text-sm font-medium transition border ${
              selected
                ? "bg-[#0055FF] text-white border-[#0055FF]"
                : "bg-white text-[#111] border-[#EAEAEA] hover:border-[#111]"
            }`}
            data-testid={`${prefix}-${opt.value}-chip`}
          >
            {opt.title}
          </button>
        );
      })}
    </div>
  );
}

function LoadingOverlay() {
  return (
    <div className="fixed inset-0 z-50 bg-white/85 backdrop-blur-sm flex flex-col items-center justify-center" data-testid="loading-overlay">
      <div className="flex items-center gap-2 mb-6">
        <span className="w-3 h-3 rounded-full bg-[#0055FF] dot-pulse" />
        <span className="w-3 h-3 rounded-full bg-[#0055FF] dot-pulse" />
        <span className="w-3 h-3 rounded-full bg-[#0055FF] dot-pulse" />
      </div>
      <p className="font-heading text-2xl font-medium">I nostri consulenti stanno cercando…</p>
      <p className="mt-3 text-sm text-[#666] max-w-md text-center">
        Selezioniamo per te le automobili più adatte al tuo profilo. Richiede pochi secondi.
      </p>
    </div>
  );
}

function ErrorHelpModal({ info, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4" data-testid="error-help-modal">
      <div className="bg-white rounded-3xl border border-[#EAEAEA] shadow-xl max-w-lg w-full p-6 md:p-8">
        <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mb-4">
          <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 9v4"/><path d="M12 17h.01"/>
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          </svg>
        </div>
        <h2 className="font-heading text-2xl font-semibold tracking-tight" data-testid="error-help-title">{info.title}</h2>
        <p className="mt-3 text-[15px] text-[#333] leading-relaxed">{info.message}</p>

        {info.suggestions?.length > 0 && (
          <div className="mt-6">
            <p className="text-xs uppercase tracking-widest text-[#0055FF] font-medium mb-3">Prova così</p>
            <ul className="space-y-2.5">
              {info.suggestions.map((s, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[#111]">
                  <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-[#E5EEFF] text-[#0055FF] text-[11px] font-semibold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <button
          onClick={onClose}
          className="mt-8 w-full h-12 rounded-full bg-[#111] text-white font-medium hover:bg-[#333] transition"
          data-testid="error-help-close"
        >
          Modifica la ricerca
        </button>
      </div>
    </div>
  );
}
